/**
 * Simple image cache utility for React Native
 * Helps prevent re-downloading images that have already been loaded
 */

interface CacheEntry {
  uri: string;
  timestamp: number;
  loaded: boolean;
}

class ImageCacheManager {
  private cache = new Map<string, CacheEntry>();
  private readonly CACHE_EXPIRY = 30 * 60 * 1000; // 30 minutes

  /**
   * Check if an image is cached and still valid
   */
  isCached(uri: string): boolean {
    const entry = this.cache.get(uri);
    if (!entry) return false;

    const now = Date.now();
    const isExpired = now - entry.timestamp > this.CACHE_EXPIRY;
    
    if (isExpired) {
      this.cache.delete(uri);
      return false;
    }

    return entry.loaded;
  }

  /**
   * Mark an image as loaded in cache
   */
  markAsLoaded(uri: string): void {
    this.cache.set(uri, {
      uri,
      timestamp: Date.now(),
      loaded: true,
    });
  }

  /**
   * Mark an image as loading (to prevent duplicate requests)
   */
  markAsLoading(uri: string): void {
    if (!this.cache.has(uri)) {
      this.cache.set(uri, {
        uri,
        timestamp: Date.now(),
        loaded: false,
      });
    }
  }

  /**
   * Clear expired entries from cache
   */
  clearExpired(): void {
    const now = Date.now();
    for (const [uri, entry] of this.cache.entries()) {
      if (now - entry.timestamp > this.CACHE_EXPIRY) {
        this.cache.delete(uri);
      }
    }
  }

  /**
   * Clear all cache entries
   */
  clearAll(): void {
    this.cache.clear();
  }

  /**
   * Get cache size
   */
  getCacheSize(): number {
    return this.cache.size;
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; loadedCount: number; loadingCount: number } {
    let loadedCount = 0;
    let loadingCount = 0;

    for (const entry of this.cache.values()) {
      if (entry.loaded) {
        loadedCount++;
      } else {
        loadingCount++;
      }
    }

    return {
      size: this.cache.size,
      loadedCount,
      loadingCount,
    };
  }
}

// Export singleton instance
export const imageCache = new ImageCacheManager();
// Interval moved to hook: useImageCacheCleanup
